import React from 'react';
import SingleSelectFilter from '@spa/features/search_job/pc/components/filter/SingleSelectFilter';
import SingleSelectFeatureFilter from '@spa/features/search_job/pc/components/filter/SingleSelectFeatureFilter';
import { FilterProvider } from '@spa/features/search_job/pc/contexts/FilterContext';
import MultiSelectFeatureFilter from "@spa/features/search_job/pc/components/filter/MultiSelectFeatureFilter";
import SalaryFilter from "@spa/features/search_job/pc/components/filter/SalaryFilter";

const FilterContent = ({
    translations,
    jobTypeOptions,
    jobTypeParamKey,
    salaryTypeOptions,
    salaryAmounts,
    salaryUnitLabels,
    salaryTypeParamKey,
    salaryMinParamKey,
    salaryMaxParamKey,
    featureParamKey,
    experienceLevelOptions,
    experienceLevelCategory,
    experienceLevelCategoryLabel,
    remoteOptions,
    remoteCategory,
    remoteCategoryLabel,
    shiftsSchedulesOptions,
    shiftsSchedulesCategory,
    shiftsSchedulesCategoryLabel,
    educationOptions,
    educationCategory,
    educationCategoryLabel,
    benefitsOptions,
    benefitsCategory,
    benefitsCategoryLabel,
    supplementalPayOptions,
    supplementalPayCategory,
    supplementalPayCategoryLabel,
    eligibilityOptions,
    eligibilityCategory,
    eligibilityCategoryLabel,
    welcomeApplicantsOptions,
    welcomeApplicantsCategory,
    welcomeApplicantsCategoryLabel,
    languageOptions,
    languageCategory,
    languageCategoryLabel,
    datePostedTypeOptions,
    datePostedParamKey,
    distanceOptions,
    distanceParamKey,
    defaultDistance,
    filterableByDistance,
    filterableByEligibility,
    filterableByWelcomeApplicants,
    filterableByLanguage,
    salaryStatistics,
}) => (
    <ul className="p-filter">
        <SingleSelectFilter
            label={translations.job_type}
            options={jobTypeOptions}
            paramKey={jobTypeParamKey}
            translations={translations}
            allJobsLabel={translations.all_jobs}
        />
        <SalaryFilter
            label={translations.salary}
            salaryCurrency={translations.salary_currency}
            upTo={translations.up_to}
            from={translations.from}
            noMin={translations.no_min}
            noMax={translations.no_max}
            applyFilter={translations.apply_filter}
            salaryTypeOptions={salaryTypeOptions}
            salaryAmounts={salaryAmounts}
            salaryUnitLabels={salaryUnitLabels}
            salaryTypeParamKey={salaryTypeParamKey}
            salaryMinParamKey={salaryMinParamKey}
            salaryMaxParamKey={salaryMaxParamKey}
            translations={translations}
            salaryStatistics={salaryStatistics}
        />
        <SingleSelectFeatureFilter
            label={experienceLevelCategoryLabel}
            options={experienceLevelOptions}
            categoryName={experienceLevelCategory}
            featureParamKey={featureParamKey}
            translations={translations}
        />
        <SingleSelectFeatureFilter
            label={remoteCategoryLabel}
            options={remoteOptions}
            categoryName={remoteCategory}
            featureParamKey={featureParamKey}
            translations={translations}
        />
        <SingleSelectFeatureFilter
            label={shiftsSchedulesCategoryLabel}
            options={shiftsSchedulesOptions}
            categoryName={shiftsSchedulesCategory}
            featureParamKey={featureParamKey}
            translations={translations}
        />
        <SingleSelectFeatureFilter
            label={educationCategoryLabel}
            options={educationOptions}
            categoryName={educationCategory}
            featureParamKey={featureParamKey}
            translations={translations}
        />
        <MultiSelectFeatureFilter
            label={benefitsCategoryLabel}
            options={benefitsOptions}
            categoryName={benefitsCategory}
            featureParamKey={featureParamKey}
            translations={translations}
        />
        <MultiSelectFeatureFilter
            label={supplementalPayCategoryLabel}
            options={supplementalPayOptions}
            categoryName={supplementalPayCategory}
            featureParamKey={featureParamKey}
            translations={translations}
        />
        {filterableByEligibility && (
            <MultiSelectFeatureFilter
                label={eligibilityCategoryLabel}
                options={eligibilityOptions}
                categoryName={eligibilityCategory}
                featureParamKey={featureParamKey}
                translations={translations}
            />
        )}
        {filterableByWelcomeApplicants && (
            <MultiSelectFeatureFilter
                label={welcomeApplicantsCategoryLabel}
                options={welcomeApplicantsOptions}
                categoryName={welcomeApplicantsCategory}
                featureParamKey={featureParamKey}
                translations={translations}
            />
        )}
        {filterableByLanguage && (
            <SingleSelectFeatureFilter
                label={languageCategoryLabel}
                options={languageOptions}
                categoryName={languageCategory}
                featureParamKey={featureParamKey}
                translations={translations}
            />
        )}
        <SingleSelectFilter
            label={translations.date_posted}
            options={datePostedTypeOptions}
            paramKey={datePostedParamKey}
            translations={translations}
            allJobsLabel={translations.all_jobs}
        />
        {filterableByDistance && (
            <SingleSelectFilter
                label={translations.distance}
                options={distanceOptions}
                paramKey={distanceParamKey}
                allJobsLabel={translations.all_jobs}
                defaultValue={defaultDistance}
                translations={translations}
            />
        )}
    </ul>
);

const FilterWrapper = ({ narrowSearchParamsViewModel, searchResultViewModel }) => {
    const mapToOptions = (data) =>
        Object.entries(data).map(([label, value]) => ({
            label,
            value: value.toString(),
        }));

    const getFeatureOptions = (key) => {
        const features = narrowSearchParamsViewModel?.feature?.[key] || [];
        const labels = narrowSearchParamsViewModel?.featureLabels || {};

        return features.map((value) => ({
            value: value.toString(),
            label: labels[value] || `Option ${value}`,
        }));
    };

    const jobTypeOptions = mapToOptions(narrowSearchParamsViewModel.jobType);
    const datePostedTypeOptions = mapToOptions(narrowSearchParamsViewModel.datePostedType);
    const distanceOptions = mapToOptions(narrowSearchParamsViewModel.distance);

    const salaryTypeOptions = narrowSearchParamsViewModel.salaryType;
    const salaryAmounts = Object.entries(narrowSearchParamsViewModel.salaryAmount).reduce(
        (acc, [key, amounts]) => ({
            ...acc,
            [key]: Object.keys(amounts),
        }),
        {}
    );
    const salaryUnitLabels = narrowSearchParamsViewModel.salaryUnitLabels;

    const experienceLevelOptions = getFeatureOptions("Experience Level");
    const remoteOptions = getFeatureOptions("Remote");
    const shiftsSchedulesOptions = getFeatureOptions("Shifts & Schedules");
    const educationOptions = getFeatureOptions("Education");
    const benefitsOptions = getFeatureOptions("Benefits");
    const supplementalPayOptions = getFeatureOptions("Supplemental Pay");
    const eligibilityOptions = getFeatureOptions("Eligibility");
    const welcomeApplicantsOptions = getFeatureOptions("Welcome Applicants");
    const languageOptions = getFeatureOptions("Language");

    return (
        <FilterProvider
            narrowSearchParamsViewModel={narrowSearchParamsViewModel}
            searchResultViewModel={searchResultViewModel}
        >
            <FilterContent
                translations={searchResultViewModel.translations.filter_items}
                jobTypeOptions={jobTypeOptions}
                jobTypeParamKey={narrowSearchParamsViewModel.jobTypeParamKey}
                salaryTypeOptions={salaryTypeOptions}
                salaryAmounts={salaryAmounts}
                salaryUnitLabels={salaryUnitLabels}
                salaryTypeParamKey={narrowSearchParamsViewModel.salaryTypeParamKey}
                salaryMinParamKey={narrowSearchParamsViewModel.salaryMinParamKey}
                salaryMaxParamKey={narrowSearchParamsViewModel.salaryMaxParamKey}
                featureParamKey={narrowSearchParamsViewModel.featureParamKey}
                experienceLevelOptions={experienceLevelOptions}
                experienceLevelCategory={narrowSearchParamsViewModel.featureCategories["Experience Level"]}
                experienceLevelCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Experience Level"]}
                remoteOptions={remoteOptions}
                remoteCategory={narrowSearchParamsViewModel.featureCategories["Remote"]}
                remoteCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Remote"]}
                shiftsSchedulesOptions={shiftsSchedulesOptions}
                shiftsSchedulesCategory={narrowSearchParamsViewModel.featureCategories["Shifts & Schedules"]}
                shiftsSchedulesCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Shifts & Schedules"]}
                educationOptions={educationOptions}
                educationCategory={narrowSearchParamsViewModel.featureCategories["Education"]}
                educationCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Education"]}
                benefitsOptions={benefitsOptions}
                benefitsCategory={narrowSearchParamsViewModel.featureCategories["Benefits"]}
                benefitsCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Benefits"]}
                supplementalPayOptions={supplementalPayOptions}
                supplementalPayCategory={narrowSearchParamsViewModel.featureCategories["Supplemental Pay"]}
                supplementalPayCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Supplemental Pay"]}
                eligibilityOptions={eligibilityOptions}
                eligibilityCategory={narrowSearchParamsViewModel.featureCategories["Eligibility"]}
                eligibilityCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Eligibility"]}
                welcomeApplicantsOptions={welcomeApplicantsOptions}
                welcomeApplicantsCategory={narrowSearchParamsViewModel.featureCategories["Welcome Applicants"]}
                welcomeApplicantsCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Welcome Applicants"]}
                languageOptions={languageOptions}
                languageCategory={narrowSearchParamsViewModel.featureCategories["Language"]}
                languageCategoryLabel={narrowSearchParamsViewModel.featureCategoryLabels["Language"]}
                datePostedTypeOptions={datePostedTypeOptions}
                datePostedParamKey={narrowSearchParamsViewModel.datePostedParamKey}
                distanceOptions={distanceOptions}
                distanceParamKey={narrowSearchParamsViewModel.distanceParamKey}
                defaultDistance={searchResultViewModel.defaultDistance}
                filterableByDistance={searchResultViewModel.filterableByDistance}
                filterableByEligibility={searchResultViewModel.filterableByEligibility}
                filterableByWelcomeApplicants={searchResultViewModel.filterableByWelcomeApplicants}
                filterableByLanguage={searchResultViewModel.filterableByLanguage}
                salaryStatistics={searchResultViewModel.salaryStatistics}
            />
        </FilterProvider>
    );
};

export default FilterWrapper;
